import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import { useAtom } from "jotai";
import { IS_DESKTOP_SCALE, IS_HORIZONTAL } from "../../../AtomStates";

const Line687 = ({ isToday = false }) => {
  return <StyledLine isToday={isToday} />;
};

const MoleculeMenuBoardInfoBoard = ({
  menuData,
  clockEnabled = true,
  logo,
  showWeeklyView = false, // showWeeklyView prop이 true면 주간 식단, false면 일일 식단을 보여줌.
  orientation, // Added orientation prop
}) => {
  const [isDesktopScale] = useAtom(IS_DESKTOP_SCALE);
  const [atomIsHorizontal] = useAtom(IS_HORIZONTAL);

  // Use orientation prop if available, otherwise fallback to atom
  const isHorizontal = orientation
    ? orientation === "landscape"
    : atomIsHorizontal;

  const dummyMenuData = {
    meal_type: ["중식", "석식"], // 기본값
    useOrigins: true,
    meal_schedule: [
      // YYYY-MM-DD 형식으로 날짜 수정
      {
        date: "2025-12-01",
        lunch_menu: [
          "백미밥",
          "시락국",
          "헐랭이수육",
          "온두부/양념장",
          "상추쌈/쌈장",
          "배추김치",
        ],
        lunch_origins: "돼지고기: 미국산",
        dinner_menu: [
          "백미밥",
          "김치햄짜글이",
          "고추잡채&꽃빵",
          "콘샐러드",
          "오징어젓갈무침",
          "배추김치",
        ],
        dinner_origins: "돼지고기: 미국산",
      },
      // ... (기존 더미 데이터 유지)
    ],
  };

  // menuData가 없으면 더미 데이터 사용
  const finalMenuData = menuData || dummyMenuData;

  // 1. 설정값 추출
  // meal_type이 없으면 기본적으로 ['중식', '석식'] (기존 로직 유지)
  const mealTypes = finalMenuData.meal_type || ["중식", "석식"];
  // useOrigins가 명시적으로 false인 경우에만 숨김 (undefined면 true 취급)
  const showOrigins = finalMenuData.use_origins !== false;

  // 헬퍼: 한글 타입을 영문 키 prefix로 변환
  const getMealKeyPrefix = (type) => {
    switch (type) {
      case "조식":
        return "breakfast";
      case "중식":
        return "lunch";
      case "석식":
        return "dinner";
      default:
        return "lunch";
    }
  };

  const today = new Date();
  // today.setHours(0, 0, 0, 0); // 타임존 이슈 방지를 위해 시간 초기화는 신중하게

  // Timezone 문제를 피하기 위해 YYYY-MM-DD 형식으로 직접 생성
  const todayString =
    today.getFullYear() +
    "-" +
    String(today.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(today.getDate()).padStart(2, "0");

  // 주간 뷰 날짜 계산 로직
  // #조식 중식 석식 (3끼) 인 경우 -> 오늘, 내일, 모레 (3일치)
  // 그 외 -> 월~금 (5일치)
  const isThreeMeals = mealTypes.length === 3;

  const displayDates = useMemo(() => {
    const dates = [];

    if (showWeeklyView && isThreeMeals) {
      // 3일치 (오늘 포함)
      for (let i = 0; i < 3; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        dates.push(d);
      }
    } else {
      // 기존 로직: 이번 주 월~금
      const mondayOfWeek = new Date(today);
      const day = mondayOfWeek.getDay();
      const diff = mondayOfWeek.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
      mondayOfWeek.setDate(diff);

      for (let i = 0; i < 5; i++) {
        const d = new Date(mondayOfWeek);
        d.setDate(mondayOfWeek.getDate() + i);
        dates.push(d);
      }
    }
    return dates;
  }, [showWeeklyView, isThreeMeals, todayString]); // today객체 대신 todayString 의존성 추천하지만, new Date()가 매번 실행되므로 일단 유지

  // 날짜 포맷팅 헬퍼
  const getDayOfWeek = (dateObj) => {
    const days = ["일", "월", "화", "수", "목", "금", "토"];
    return days[dateObj.getDay()];
  };

  // 날짜 문자열(YYYY-MM-DD) 생성 헬퍼
  const getDateString = (dateObj) => {
    return (
      dateObj.getFullYear() +
      "-" +
      String(dateObj.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(dateObj.getDate()).padStart(2, "0")
    );
  };

  const mealSchedule = finalMenuData?.meal_schedule || [];

  // 우측 패널 메타(시각/온도) - 현재 디자인상 사용되지 않을 수 있으나 유지
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    if (!clockEnabled) return;
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [clockEnabled]);

  // 오늘 식단 데이터 찾기
  const todaysMealData = mealSchedule.find((day) => day.date === todayString);

  return (
    <FullScreenContainer>
      <AspectRatioBox isHorizontal={isHorizontal}>
        <StyledContainer
          isDesktopScale={isDesktopScale}
          isHorizontal={isHorizontal}
        >
          <LogoSection>
            {logo && <LogoImage src={logo} alt="로고" />}
          </LogoSection>

          <TopSection>
            <TitleSection>
              {/* 타이틀 영역이 필요하면 주석 해제 */}
            </TitleSection>
          </TopSection>

          {/* 메인 콘텐츠 영역 */}
          <ContentSection>
            {showWeeklyView ? (
              /* ================= 주간 식단표 ================= */
              <WeeklyCardList isHorizontal={isHorizontal}>
                {displayDates.map((dateObj) => {
                  const dateStr = getDateString(dateObj);
                  const dayData = mealSchedule.find((d) => d.date === dateStr);
                  const isToday = dateStr === todayString;

                  return (
                    <DayColumn
                      key={dateStr}
                      isToday={isToday}
                      isHorizontal={isHorizontal}
                      count={displayDates.length} // 카드가 몇 개인지 전달하여 너비 조절 가능하게 함
                    >
                      <DayHeader isToday={isToday} isHorizontal={isHorizontal}>
                        <DayDateText>
                          {dateObj.getMonth() + 1}/{dateObj.getDate()} (
                          {getDayOfWeek(dateObj)})
                        </DayDateText>
                      </DayHeader>

                      {/* 설정된 식단 타입(조,중,석)에 따라 반복 렌더링 */}
                      {mealTypes.map((type, idx) => {
                        const prefix = getMealKeyPrefix(type);
                        const menuList = dayData?.[`${prefix}_menu`] || [];
                        const origin = dayData?.[`${prefix}_origins`] || "";
                        const hasMenu = menuList.length > 0;

                        return (
                          <React.Fragment key={idx}>
                            <WeeklyMealSection>
                              <WeeklyCategoryTitle isHorizontal={isHorizontal}>
                                {type}
                              </WeeklyCategoryTitle>
                              {hasMenu ? (
                                <WeeklyMenuList isHorizontal={isHorizontal} count={mealTypes.length}>
                                  {menuList.map((item, itemIdx) => (
                                    <WeeklyMenuItem
                                      key={itemIdx}
                                      isHorizontal={isHorizontal}
                                    >
                                      {item}
                                    </WeeklyMenuItem>
                                  ))}
                                </WeeklyMenuList>
                              ) : (
                                <WeeklyMenuText>정보 없음</WeeklyMenuText>
                              )}
                              {showOrigins && (
                                <WeeklyOriginText isHorizontal={isHorizontal}>
                                  {origin}
                                </WeeklyOriginText>
                              )}
                            </WeeklyMealSection>
                            {/* 마지막 아이템이 아니면 구분선 추가 */}
                            {idx < mealTypes.length - 1 && (
                              <WeeklyDivider isHorizontal={isHorizontal} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </DayColumn>
                  );
                })}
              </WeeklyCardList>
            ) : (
              /* ================= 일일 식단표 ================= */
              <DynamicMealGrid
                isHorizontal={isHorizontal}
                count={mealTypes.length}
              >
                {mealTypes.map((type, index) => {
                  const prefix = getMealKeyPrefix(type);
                  const menuList = todaysMealData?.[`${prefix}_menu`] || [];
                  const origin = todaysMealData?.[`${prefix}_origins`] || "";
                  const displayMenu =
                    menuList.length > 0
                      ? menuList.join("\n")
                      : "식단 정보가 없습니다.";
                  const isLast = index === mealTypes.length - 1;

                  return (
                    <MenuCard
                      key={type}
                      isHorizontal={isHorizontal}
                      count={mealTypes.length}
                    >
                      <MenuDateSection>
                        <MenuDate isHorizontal={isHorizontal}>{type}</MenuDate>
                        {/* 마지막 카드에만 시계 표시 등의 로직이 필요하면 여기에 추가 */}
                        {/* {isLast && clockEnabled && ...} */}
                      </MenuDateSection>
                      <Line687 />
                      <MenuItems
                        isToday={true}
                        isHorizontal={isHorizontal}
                        count={mealTypes.length}
                      >
                        {displayMenu.split("\n").map((item, idx) => (
                          <span key={idx} style={{ display: "block" }}>
                            {item}
                          </span>
                        ))}
                      </MenuItems>
                      {showOrigins && origin && (
                        <OriginText isHorizontal={isHorizontal}>
                          {origin}
                        </OriginText>
                      )}
                    </MenuCard>
                  );
                })}
              </DynamicMealGrid>
            )}
          </ContentSection>
        </StyledContainer>
      </AspectRatioBox>
    </FullScreenContainer>
  );
};

export default MoleculeMenuBoardInfoBoard;

// 스타일 컴포넌트들
const FullScreenContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const AspectRatioBox = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background: transparent;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const TopSection = styled.div`
  width: 100%;
  height: auto;
  padding: 0;
  box-sizing: border-box;
  align-items: center;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 12px;
  z-index: 1;
`;

const LogoSection = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 50%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.2;
  z-index: 0;
  pointer-events: none;
`;

const LogoImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
`;

const TitleSection = styled.div`
  position: relative;
  left: 0;
  top: 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-direction: column;
`;

const ContentSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  overflow: hidden;
  position: relative;
  z-index: 1;
`;

// 기존 TwoCol 대체: 식단 개수에 따라 레이아웃 대응
const DynamicMealGrid = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  gap: 12px;
  justify-content: space-between;

  /* 기본: 세로 배치 (모바일/세로모드) */
  flex-direction: column;

  /* 세로 모드일 때 (Horizontal이 아닐 때) */
  ${({ isHorizontal }) =>
    !isHorizontal &&
    `
    gap: 8px;
  `}

  /* 가로 모드일 때 (Landscape) */
  ${({ isHorizontal }) =>
    isHorizontal &&
    `
    flex-direction: row;
    gap: 24px;
    height: auto;
  `}
`;

// 기존 MenuContainer 통합
const MenuCard = styled.div`
  padding: 20px;
  height: auto;
  width: 100%;
  background: transparent;
  box-shadow: none;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1; /* 가용 공간 균등 분할 */

  /* 가로 모드일 때 너비 조정 */
  ${({ isHorizontal, count }) =>
    isHorizontal
      ? `
    width: ${100 / count}%;
  `
      : `
    justify-content: flex-start; 
  `}
`;

const MenuDateSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  margin-bottom: 8px;
`;

const MenuDate = styled.span`
  color: #323232;
  font-size: calc(3 * var(--font-scaler));
  font-family: Pretendard;
  font-weight: 700;
  line-height: 1.2;
  text-align: start;

  ${({ isHorizontal }) =>
    !isHorizontal &&
    `
    font-size: calc(4 * var(--font-scaler));
  `}
`;

const MenuItems = styled.div`
  color: #323232;
  font-size: 40px;
  font-family: Pretendard;
  font-weight: 600;
  white-space: pre-line;
  text-align: center;
  line-height: 1.6;

  /* 식단 개수가 많아지면 폰트 사이즈 조정 (선택적) */
  ${({ count }) =>
    count >= 3 &&
    `
     font-size: 32px; 
  `}

  ${({ isHorizontal, count }) =>
    !isHorizontal &&
    `
    font-size: calc(2.4 * var(--font-scaler));
    line-height: 1.4;
    font-weight: 700;
    
    /* 2열 그리드 배치 */
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 32px;
    justify-content: center;
    align-items: center;
    
    white-space: normal;

    /* 3개 식단일 경우 공간이 부족할 수 있으므로 폰트 조정 */
    ${
      count >= 3
        ? `
      font-size: calc(2.0 * var(--font-scaler));
      gap: 8px 16px;
    `
        : ""
    }
  `}
`;

const OriginText = styled.div`
  color: rgba(50, 50, 50, 0.7);
  font-size: 24px;
  font-family: Pretendard;
  font-weight: 500;
  line-height: 1.4;
  margin-top: 16px; //auto;
  white-space: pre-line;
  text-align: center;

  ${({ isHorizontal }) =>
    !isHorizontal &&
    `
    font-size: 28px;
  `}
`;

const StyledLine = styled.div`
  width: 100%;
  height: 3px;
  background: #323232;
  margin-bottom: 0px;
`;

const DayDateText = styled.span`
  font-family: Pretendard;
  font-weight: 700;
  font-size: 24px;
  color: #323232;
  white-space: pre-wrap;
`;

// Add styles
const WeeklyCardList = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: space-between;
  margin-top: 12px;

  ${({ isHorizontal }) =>
    !isHorizontal &&
    `
    flex-direction: column;
  `}
`;

const DayColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: ${(props) =>
    props.isToday ? "rgba(255, 255, 255, 0.6)" : "rgba(255, 255, 255, 0.3)"};
  border-radius: 12px;
  padding: 12px 8px;
  gap: 8px;
  border: ${(props) =>
    props.isToday ? "2px solid #323232" : "1px solid rgba(50,50,50,0.1)"};
  box-shadow: ${(props) =>
    props.isToday ? "0 4px 12px rgba(0,0,0,0.1)" : "none"};
  align-items: stretch;

  ${({ isHorizontal }) =>
    !isHorizontal &&
    `
    flex-direction: row;
    align-items: center;
  `}
`;

const DayHeader = styled.div`
  text-align: center;
  padding-bottom: 8px;
  border-bottom: 2px solid #323232;
  margin-bottom: 4px;

  ${({ isHorizontal }) =>
    !isHorizontal &&
    `
    width: 15%;
    min-width: 80px;
    border-bottom: none;
    border-right: 2px solid #323232;
    padding-bottom: 0;
    padding-right: 8px;
    margin-bottom: 0;
    margin-right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  `}
`;

const WeeklyMealSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
  height: 100%;
  justify-content: center;
`;

const WeeklyCategoryTitle = styled.div`
  font-family: Pretendard;
  font-weight: 700;
  font-size: 22px;
  color: #323232;
  margin-bottom: 4px;
  text-align: center;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  padding: 2px 0;

  ${({ isHorizontal }) =>
    !isHorizontal &&
    `
    font-size: 25px;
    padding: 4px 0;
  `}
`;

const WeeklyMenuText = styled.div`
  font-family: Pretendard;
  font-size: 22px;
  color: #323232;
  white-space: pre-line;
  line-height: 1.4;
  text-align: center;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WeeklyMenuList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 4px 8px;
  flex: 1;
  overflow-y: auto;
  align-content: start;
  padding: 4px;

  /* 가로 모드 & 3끼: 2열 */
  ${({ isHorizontal, count }) =>
    isHorizontal &&
    count === 3 &&
    `
    grid-template-columns: 1fr 1fr;
  `}

  ${({ isHorizontal, count }) =>
    !isHorizontal &&
    `
    /* 세로 모드 기본: 2열 */
    grid-template-columns: 1fr 1fr;
    align-content: center top;

    /* 세로 모드 & 3끼: 1열 */
    ${
      count === 3
        ? `
      grid-template-columns: 1fr;
    `
        : ""
    }
  `}
`;

const WeeklyMenuItem = styled.div`
  font-family: Pretendard;
  font-size: 19px;
  color: #323232;
  text-align: center;
  word-break: keep-all;
  line-height: 1.3;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;

  ${({ isHorizontal }) =>
    !isHorizontal &&
    `
    font-size: 22px;
    line-height: 1.4;
  `}
`;

const WeeklyOriginText = styled.div`
  font-family: Pretendard;
  font-size: 16px;
  color: #666;
  margin-top: auto;
  text-align: center;

  ${({ isHorizontal }) =>
    !isHorizontal &&
    `
    font-size: 18px;
  `}
`;

const WeeklyDivider = styled.div`
  width: 100%;
  height: 1px;
  background: #ccc;
  margin: 4px 0;
  flex-shrink: 0;

  ${({ isHorizontal }) =>
    !isHorizontal &&
    `
    width: 1px;
    height: 100%;
    margin: 0 4px;
  `}
`;